'use client';

import { LinkButton } from '@/components/ui/Button/LinkButton';
import { Icon } from '@/components/ui/Icon';
import { getStoreOpenStatus } from '@/lib/boutique/hours';
import {
  buildBoutiqueAddressLines,
  buildBoutiqueMapsUrl,
  getBoutiqueHoursMessage,
  getBoutiqueOpenStatusLabel,
} from '@/lib/boutique/storeDisplayUtils';
import type { StoreHit } from '@/lib/algolia/stores';
import { Link } from '@/lib/i18n/navigation';
import { boutiquePaths } from '@/lib/paths';
import { getTranslatedStoreValue } from '@/lib/stores/utils';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import Image from 'next/image';

import styles from './BoutiqueListingCard.module.scss';

export interface BoutiqueListingCardProps {
  store: StoreHit;
  localeSlug: string;
  countryCode: string;
  index?: number;
}

function ChevronLinkIcon() {
  return <Icon name="chevron-right" size={14} className={styles.linkIcon} aria-hidden="true" />;
}

/**
 * Boutique listing card for the boutiques page grid.
 */
export function BoutiqueListingCard({
  store,
  localeSlug,
  countryCode,
  index,
}: Readonly<BoutiqueListingCardProps>) {
  const t = useTranslations('boutiquesPage');
  const tCommon = useTranslations('common');
  const tBoutique = useTranslations('boutique');
  const openStatus = getStoreOpenStatus(store);

  const name = getTranslatedStoreValue(store, 'name', localeSlug);
  const street = getTranslatedStoreValue(store, 'street', localeSlug);
  const street2 = getTranslatedStoreValue(store, 'street2', localeSlug);
  const city = getTranslatedStoreValue(store, 'city', localeSlug);
  const region = getTranslatedStoreValue(store, 'region', localeSlug);
  const postalCode = getTranslatedStoreValue(store, 'postal_code', localeSlug);
  const countryLabel =
    new Intl.DisplayNames([localeSlug], { type: 'region' }).of(countryCode) ?? countryCode;

  const storeImage = store.photo_gallery?.[0]?.image?.url || store.thumbnail_url;
  const detailUrl = boutiquePaths.detail(store.slug);
  const appointmentUrl = boutiquePaths.appointment(store.slug);
  const mapsUrl = buildBoutiqueMapsUrl(store, street, city);

  const addressLines = buildBoutiqueAddressLines({
    countryCode,
    localeSlug,
    street,
    street2,
    city,
    region,
    postalCode,
  });

  const email = Array.isArray(store.emails) ? store.emails[0] : undefined;
  const hoursMessage = getBoutiqueHoursMessage(openStatus, {
    opensUntil: (values) => t('opensUntil', values),
    opensAt: (values) => t('opensAt', values),
    weekday: (weekday) => tCommon(`weekdays.${weekday}`),
  });
  const statusLabel = getBoutiqueOpenStatusLabel(openStatus.status, {
    open: t('openNow'),
    closed: t('closed'),
    temporary: t('temporaryClosed'),
  });

  return (
    <article
      className={styles.card}
      data-testid={`boutique-listing-card-${index ?? store.store_id}`}
    >
      <Link href={detailUrl} className={styles.imageLink}>
        <div className={styles.imageWrapper}>
          {storeImage ? (
            <Image
              src={storeImage}
              alt={name}
              fill
              className={styles.image}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            />
          ) : (
            <div className={styles.imagePlaceholder} aria-hidden="true" />
          )}
        </div>
      </Link>

      <div className={styles.body}>
        <h2 className={styles.name}>{name}</h2>

        <div className={styles.statusRow}>
          <div className={styles.statusLeft}>
            <span
              className={clsx(styles.statusDot, styles[`statusDot_${openStatus.status}`])}
              aria-hidden="true"
            />
            <span className={styles.statusText}>{statusLabel}</span>
          </div>
          {hoursMessage ? <span className={styles.hoursText}>{hoursMessage}</span> : null}
        </div>

        <address className={styles.address}>
          {addressLines.map((line) => (
            <span key={line} className={styles.addressLine}>
              {line}
            </span>
          ))}
          <span className={styles.addressLine}>{countryLabel}</span>
        </address>

        <div className={styles.contact}>
          {store.phone ? (
            <p className={styles.contactLine}>
              <span className={styles.contactLabel}>{t('phone')}</span>
              <a href={`tel:${store.phone}`} className={styles.contactValue}>
                {store.phone}
              </a>
            </p>
          ) : null}
          {email ? (
            <p className={styles.contactLine}>
              <span className={styles.contactLabel}>{t('email')}</span>
              <a href={`mailto:${email}`} className={styles.contactValue}>
                {t('sendAnEmail')}
              </a>
            </p>
          ) : null}
        </div>

        <div className={styles.links}>
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className={styles.textLink}>
            {tBoutique('getDirections')}
            <ChevronLinkIcon />
          </a>
          <Link href={detailUrl} className={styles.textLink}>
            {tBoutique('viewMore')}
            <ChevronLinkIcon />
          </Link>
        </div>

        {store.offers_appointment ? (
          <LinkButton
            href={appointmentUrl}
            variant="secondary"
            size="md"
            className={styles.ctaButton}
            iconAfter={<ChevronLinkIcon />}
          >
            {t('bookAppointment')}
          </LinkButton>
        ) : (
          <LinkButton
            href={detailUrl}
            variant="secondary"
            size="md"
            className={styles.ctaButton}
            iconAfter={<ChevronLinkIcon />}
          >
            {t('viewDetails')}
          </LinkButton>
        )}
      </div>
    </article>
  );
}

BoutiqueListingCard.displayName = 'BoutiqueListingCard';
